// Keeps a booking's generic "extra" table rows (BioExtra, ArrestReportsExtra)
// loaded with their real PK regardless of which Bookings tab the user
// actually visits.
//
// AdditionalPersonalInformation.jsx / AdditionalBookingInfo.jsx (BioExtra /
// ArrestReportsExtra's default fields) each only run their own effects once
// they mount — and each lives on its own lazily-mounted tab (antd Tabs only
// mounts a pane the first time it's activated). BoookingsReleaseInformation
// .jsx and ArrestBook.jsx read and write the very same
// bookings.arrestReports[0].arrestReportsExtra[0] row (just a different
// subset of its _U columns, routed there via client params) but have no
// fetch of their own — so a user who opens "Release Information" without
// ever visiting "Additional Booking Information" would edit a row whose real
// PK was never loaded, and a save would wrongly send CREATE against a row
// that already exists.
//
// These two hooks are called once from BookingsDetails/index.jsx (always
// mounted whenever a booking is open, unlike any one tab's content) — the
// single source of truth for this fetch. (An earlier version of this fix
// also kept a duplicate copy of the same fetch inside
// AdditionalPersonalInformation.jsx / AdditionalBookingInfo.jsx themselves,
// on the assumption that "whichever runs first wins" — that's wrong for an
// async fetch: both copies' own private "in flight" ref would see no PK yet
// and both would fire a GET before either's response landed back in redux,
// so they never actually deduplicated against each other. Those copies were
// removed; this hook is now the only place that performs this fetch.)
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setBookings } from "@/slices/bookingsSlice";
import { fetchExtraTableRecord } from "@/util/extraColumnActions";

const BIO_EXTRA_ID_KEY = "bioExtraID";
const BIO_ID_COLUMN = "bioID";

// NOTE: despite the table being named "ArrestReportsExtra" (plural), the API
// serializes its PK as "arrestReportExtraID" — singular "Report" — see the
// matching note in AdditionalBookingInfo.jsx.
const ARREST_REPORTS_EXTRA_ID_KEY = "arrestReportExtraID";
const ARREST_REPORT_ID_COLUMN = "arrestReportID";

const INMATE_PROPERTY_EXTRA_ID_KEY = "inmatePropertyExtraID";
const INMATE_PROPERTY_ID_COLUMN = "inmatePropertyID";

const INMATE_MEDICAL_EXTRA_ID_KEY = "inmateMedicalExtraID";
const INMATE_MEDICAL_ID_COLUMN = "inmateMedicalID";

export const useBioExtraSync = () => {
  const dispatch = useDispatch();
  const bookings = useSelector((state) => state?.bookings?.bookings);
  const bookingsRef = useRef(bookings);
  useEffect(() => {
    bookingsRef.current = bookings;
  }, [bookings]);

  const fetchInFlightBioIdRef = useRef(null);

  useEffect(() => {
    const bioId = bookings?.bio?.[0]?.bioId;
    if (!bioId) return;

    const currentExtraId = bookings?.bio?.[0]?.bioExtra?.[0]?.[BIO_EXTRA_ID_KEY];
    if (currentExtraId) return; // already have the real, saved row's PK

    if (fetchInFlightBioIdRef.current === bioId) return; // already fetching this id
    fetchInFlightBioIdRef.current = bioId;

    fetchExtraTableRecord("BioExtra", BIO_ID_COLUMN, bioId)
      .then((result) => {
        if (!result) return;
        const bio = bookingsRef.current?.bio?.[0] || {};
        const localExtra = bio?.bioExtra?.[0] || {};
        dispatch(
          setBookings({
            bio: [
              {
                ...bio,
                bioExtra: [
                  {
                    ...result.values,
                    ...localExtra, // preserve any edits typed before this resolved
                    [BIO_EXTRA_ID_KEY]:
                      result.raw?.BioExtraID ?? localExtra?.[BIO_EXTRA_ID_KEY] ?? 0,
                    [BIO_ID_COLUMN]: bioId,
                  },
                ],
              },
            ],
          }),
        );
      })
      .catch((error) => {
        console.error(
          "[useBioExtraSync] Request failed:",
          error.response ? error.response.data : error.message,
        );
      })
      .finally(() => {
        if (fetchInFlightBioIdRef.current === bioId) {
          fetchInFlightBioIdRef.current = null;
        }
      });
  }, [
    bookings?.bio?.[0]?.bioId,
    bookings?.bio?.[0]?.bioExtra?.[0]?.[BIO_EXTRA_ID_KEY],
  ]);
};

export const useArrestReportsExtraSync = () => {
  const dispatch = useDispatch();
  const bookings = useSelector((state) => state?.bookings?.bookings);
  const bookingsRef = useRef(bookings);
  useEffect(() => {
    bookingsRef.current = bookings;
  }, [bookings]);

  const fetchInFlightArrestReportIdRef = useRef(null);

  useEffect(() => {
    const arrestReportId = bookings?.arrestReports?.[0]?.arrestReportId;
    if (!arrestReportId) return;

    const currentExtraId =
      bookings?.arrestReports?.[0]?.arrestReportsExtra?.[0]?.[
        ARREST_REPORTS_EXTRA_ID_KEY
      ];
    if (currentExtraId) return; // already have the real, saved row's PK

    if (fetchInFlightArrestReportIdRef.current === arrestReportId) return; // already fetching this id
    fetchInFlightArrestReportIdRef.current = arrestReportId;

    fetchExtraTableRecord(
      "ArrestReportsExtra",
      ARREST_REPORT_ID_COLUMN,
      arrestReportId,
    )
      .then((result) => {
        if (!result) return;
        const arrestReport = bookingsRef.current?.arrestReports?.[0] || {};
        const localExtra = arrestReport?.arrestReportsExtra?.[0] || {};
        dispatch(
          setBookings({
            arrestReports: [
              {
                ...arrestReport,
                arrestReportsExtra: [
                  {
                    ...result.values,
                    ...localExtra, // preserve any edits typed before this resolved
                    [ARREST_REPORTS_EXTRA_ID_KEY]:
                      result.raw?.ArrestReportExtraID ??
                      localExtra?.[ARREST_REPORTS_EXTRA_ID_KEY] ??
                      0,
                    [ARREST_REPORT_ID_COLUMN]: arrestReportId,
                  },
                ],
              },
            ],
          }),
        );
      })
      .catch((error) => {
        console.error(
          "[useArrestReportsExtraSync] Request failed:",
          error.response ? error.response.data : error.message,
        );
      })
      .finally(() => {
        if (fetchInFlightArrestReportIdRef.current === arrestReportId) {
          fetchInFlightArrestReportIdRef.current = null;
        }
      });
  }, [
    bookings?.arrestReports?.[0]?.arrestReportId,
    bookings?.arrestReports?.[0]?.arrestReportsExtra?.[0]?.[
      ARREST_REPORTS_EXTRA_ID_KEY
    ],
  ]);
};

// Same idea as useBioExtraSync/useArrestReportsExtraSync, for
// InmatePropertyExtra — Property.jsx (the Inmate Property tabs) reads/writes
// bookings.inmateProperty[0].inmatePropertyExtra[0], and is used from two
// separate lazily-mounted tabs ("Property Taken From Inmate" / "Property
// List") in BookingsDetails/index.jsx, so it has the exact same "visiting
// the wrong tab first never loads the real PK" gap.
export const useInmatePropertyExtraSync = () => {
  const dispatch = useDispatch();
  const bookings = useSelector((state) => state?.bookings?.bookings);
  const bookingsRef = useRef(bookings);
  useEffect(() => {
    bookingsRef.current = bookings;
  }, [bookings]);

  const fetchInFlightInmatePropertyIdRef = useRef(null);

  useEffect(() => {
    const inmatePropertyId = bookings?.inmateProperty?.[0]?.inmatePropertyId;
    if (!inmatePropertyId) return;

    const currentExtraId =
      bookings?.inmateProperty?.[0]?.inmatePropertyExtra?.[0]?.[
        INMATE_PROPERTY_EXTRA_ID_KEY
      ];
    if (currentExtraId) return; // already have the real, saved row's PK

    if (fetchInFlightInmatePropertyIdRef.current === inmatePropertyId) return; // already fetching this id
    fetchInFlightInmatePropertyIdRef.current = inmatePropertyId;

    fetchExtraTableRecord(
      "InmatePropertyExtra",
      INMATE_PROPERTY_ID_COLUMN,
      inmatePropertyId,
    )
      .then((result) => {
        if (!result) return;
        const inmateProperty = bookingsRef.current?.inmateProperty?.[0] || {};
        const localExtra = inmateProperty?.inmatePropertyExtra?.[0] || {};
        dispatch(
          setBookings({
            inmateProperty: [
              {
                ...inmateProperty,
                inmatePropertyExtra: [
                  {
                    ...result.values,
                    ...localExtra, // preserve any edits typed before this resolved
                    [INMATE_PROPERTY_EXTRA_ID_KEY]:
                      result.raw?.InmatePropertyExtraID ??
                      localExtra?.[INMATE_PROPERTY_EXTRA_ID_KEY] ??
                      0,
                    [INMATE_PROPERTY_ID_COLUMN]: inmatePropertyId,
                  },
                ],
              },
            ],
          }),
        );
      })
      .catch((error) => {
        console.error(
          "[useInmatePropertyExtraSync] Request failed:",
          error.response ? error.response.data : error.message,
        );
      })
      .finally(() => {
        if (fetchInFlightInmatePropertyIdRef.current === inmatePropertyId) {
          fetchInFlightInmatePropertyIdRef.current = null;
        }
      });
  }, [
    bookings?.inmateProperty?.[0]?.inmatePropertyId,
    bookings?.inmateProperty?.[0]?.inmatePropertyExtra?.[0]?.[
      INMATE_PROPERTY_EXTRA_ID_KEY
    ],
  ]);
};

// Same idea as useBioExtraSync/useArrestReportsExtraSync/
// useInmatePropertyExtraSync, for InmateMedicalExtra —
// AdditionalInmateMedicalInfo.jsx (the "Additional Inmate Medical Info" tab)
// reads/writes bookings.inmateMedical[0].inmateMedicalExtra[0]; centralizing
// the fetch here (rather than inside that tab's own component) avoids the
// same "duplicate/racing fetch" bug class the other three hooks were built
// to fix.
//
// InmateMedical is a normal onetoone entity on the booking (see
// bookingTree.js — same relation/referenceColumn shape as bio/
// arrestReports), so its own id is expected to come back nested under
// bookings.inmateMedical[0].inmateMedicalId from the ordinary booking GET,
// exactly like bioId/arrestReportId/inmatePropertyId — no separate resolve
// call against InmateMedical itself (that generic /api/extra/... mechanism
// is for _Extra tables, not core ones).
export const useInmateMedicalExtraSync = () => {
  const dispatch = useDispatch();
  const bookings = useSelector((state) => state?.bookings?.bookings);
  const bookingsRef = useRef(bookings);
  useEffect(() => {
    bookingsRef.current = bookings;
  }, [bookings]);

  const fetchInFlightInmateMedicalIdRef = useRef(null);

  useEffect(() => {
    const inmateMedicalId = bookings?.inmateMedical?.[0]?.inmateMedicalId;
    if (!inmateMedicalId) return;

    const currentExtraId =
      bookings?.inmateMedical?.[0]?.inmateMedicalExtra?.[0]?.[
        INMATE_MEDICAL_EXTRA_ID_KEY
      ];
    if (currentExtraId) return; // already have the real, saved row's PK

    if (fetchInFlightInmateMedicalIdRef.current === inmateMedicalId) return; // already fetching this id
    fetchInFlightInmateMedicalIdRef.current = inmateMedicalId;

    fetchExtraTableRecord(
      "InmateMedicalExtra",
      INMATE_MEDICAL_ID_COLUMN,
      inmateMedicalId,
    )
      .then((result) => {
        if (!result) return;
        const inmateMedical = bookingsRef.current?.inmateMedical?.[0] || {};
        const localExtra = inmateMedical?.inmateMedicalExtra?.[0] || {};
        dispatch(
          setBookings({
            inmateMedical: [
              {
                ...inmateMedical,
                inmateMedicalExtra: [
                  {
                    ...result.values,
                    ...localExtra, // preserve any edits typed before this resolved
                    [INMATE_MEDICAL_EXTRA_ID_KEY]:
                      result.raw?.InmateMedicalExtraID ??
                      localExtra?.[INMATE_MEDICAL_EXTRA_ID_KEY] ??
                      0,
                    [INMATE_MEDICAL_ID_COLUMN]: inmateMedicalId,
                  },
                ],
              },
            ],
          }),
        );
      })
      .catch((error) => {
        console.error(
          "[useInmateMedicalExtraSync] Request failed:",
          error.response ? error.response.data : error.message,
        );
      })
      .finally(() => {
        if (fetchInFlightInmateMedicalIdRef.current === inmateMedicalId) {
          fetchInFlightInmateMedicalIdRef.current = null;
        }
      });
  }, [
    bookings?.inmateMedical?.[0]?.inmateMedicalId,
    bookings?.inmateMedical?.[0]?.inmateMedicalExtra?.[0]?.[
      INMATE_MEDICAL_EXTRA_ID_KEY
    ],
  ]);
};
